import { z } from 'zod';

import { baseOrganizationEntitySchema } from './organizationContract';
import { FeedSource, FeedStatus, UpdateFrequency } from '../enums';

export const FeedSchema = baseOrganizationEntitySchema.extend({
  name: z.string().max(100),
  url: z.string().url(),
  source: z.nativeEnum(FeedSource),
  /**
   * Agents the feed is assigned to. At least one: until there is an
   * organization-level feeds page, a feed with no agent would import at cost
   * with nobody able to see or use it.
   */
  chatIds: z.array(z.string().uuid()).min(1),
  scheduledAt: z.coerce.date().nullish(),
  content: z.string().nullish(),
  updateFrequency: z.nativeEnum(UpdateFrequency),
  status: z.nativeEnum(FeedStatus).nullish(),
  importMetadata: z
    .object({
      totalProducts: z.number().int().nonnegative().optional(),
      phase: z
        .enum([
          'downloading',
          'parsing',
          'importing',
          'scraping',
          'enriching',
          'embedding',
        ])
        .optional(),
      stageProgress: z
        .object({
          processed: z.number().nonnegative(),
          total: z.number().nonnegative(),
          failed: z.number().nonnegative(),
        })
        .optional(),
      retries: z.number().int().positive().nullish(),
      lastError: z.string().nullish(),
      productErrors: z
        .array(
          z.object({
            externalId: z.string(),
            message: z.string(),
          }),
        )
        .nullish(),
      parserErrors: z
        .array(z.object({ externalId: z.string(), message: z.string() }))
        .nullish(),
      failedProductExternalIds: z.array(z.string()).nullish(),
      productImport: z
        .object({
          total: z.number().int().nonnegative(),
          imported: z.number().int().nonnegative(),
          unchanged: z.number().int().nonnegative(),
          parserSkipped: z.number().int().nonnegative(),
          failed: z.number().int().nonnegative(),
        })
        .nullish(),
    })
    .nullish(),

  /**
   * Paid, per-feed enrichment and its inputs, off by default and editable
   * from the feed settings UI. Defaulted rather than required so existing
   * create calls keep working while the parsed value is still a real boolean
   * for the entity.
   */
  processingPaused: z.boolean().default(false),
  enrichProducts: z.boolean().default(false),
  scrapeProductPages: z.boolean().default(false),
  annotateImages: z.boolean().default(false),
});

export const createFeedSchema = FeedSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  organization: true,
  status: true,
  importMetadata: true,
  scheduledAt: true,
  content: true,
  processingPaused: true,
}).strict();

export const updateFeedSchema = FeedSchema.partial()
  .required({
    id: true,
  })
  .omit({
    organization: true,
    processingPaused: true,
  })
  .strict();

export type FeedDTO = z.infer<typeof FeedSchema>;
export type CreateFeedDTO = z.infer<typeof createFeedSchema>;
export type UpdateFeedDTO = z.infer<typeof updateFeedSchema>;

export const feedCostEstimateInputSchema = z.object({
  feedId: z.string().uuid().optional(),
  url: z.string().url(),
  source: z.nativeEnum(FeedSource),
  enrichProducts: z.boolean().default(false),
  scrapeProductPages: z.boolean(),
  annotateImages: z.boolean(),
});
const costRangeSchema = z.object({
  low: z.number().nonnegative(),
  high: z.number().nonnegative(),
});

export const feedCostEstimateSchema = z.object({
  currency: z.literal('USD'),
  productCount: z.number().int().nonnegative(),
  countSource: z.enum(['catalog', 'feed']),
  enrichmentBasis: z.enum(['history', 'baseline']),
  perProduct: z.object({
    enrichment: costRangeSchema,
    embeddings: costRangeSchema,
    scraping: costRangeSchema,
    images: costRangeSchema,
  }),
  perProductTotal: costRangeSchema,
  total: costRangeSchema,
});
export type FeedCostEstimateInput = z.infer<typeof feedCostEstimateInputSchema>;
export type FeedCostEstimate = z.infer<typeof feedCostEstimateSchema>;
